import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { BackButton } from '../../common/BackButton';
import { contactService } from '../../../services/contactService';
import { formatContactFullName } from '../../../utils/contactName';
import { useDebtLedger } from '../hooks/useDebtLedger';
import RecordPaymentDialog from './RecordPaymentDialog';
import type { DebtDirection } from '../../../types/debt';

export default function DebtLedgerPage() {
  const { id, contactId } = useParams<{ id: string; contactId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  if (!id || !contactId) {
    throw new Error('Company ID and Contact ID are required');
  }

  const companyId = parseInt(id);
  const contactIdNum = parseInt(contactId);
  const direction: DebtDirection = searchParams.get('direction') === 'WE_OWE' ? 'WE_OWE' : 'CLIENT_OWES';

  const [contactName, setContactName] = useState<string | null>(null);
  const [contactLoadError, setContactLoadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    data: { entries, balance, loading, error },
    pagination: { page, count: totalPages, onChange: handlePageChange },
    actions: { recordPayment, deletePayment },
  } = useDebtLedger(contactIdNum, direction);

  useEffect(() => {
    let cancelled = false;

    contactService.getById(contactIdNum)
      .then((contact) => {
        if (cancelled) {
          return;
        }
        setContactName(formatContactFullName(contact));
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load contact:', err);
        setContactLoadError('Не вдалося завантажити контакт');
      });

    return () => {
      cancelled = true;
    };
  }, [contactIdNum, companyId]);

  const handleDeletePayment = async (paymentId: number) => {
    setDeleteError(null);
    try {
      await deletePayment(paymentId);
    } catch (err) {
      console.error('Failed to delete payment:', err);
      setDeleteError('Не вдалося видалити оплату. Спробуйте ще раз.');
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <BackButton
        label="Назад"
        onClick={() => navigate(`/companies/${companyId}/debts`)}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h1">
            {contactName ?? '...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {direction === 'CLIENT_OWES' ? 'Винен нам' : 'Ми винні'}
          </Typography>
        </Box>
        <Typography variant="h4" color={balance > 0 ? 'error' : 'text.primary'}>
          ${balance.toFixed(2)}
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ mb: 3 }}
      >
        Записати оплату
      </Button>

      {(error || contactLoadError || deleteError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || contactLoadError || deleteError}
        </Alert>
      )}

      {loading && entries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {entries.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Записів ще немає
            </Typography>
          ) : (
            <List>
              {entries.map((entry) => {
                const itemText = (
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={entry.entryType === 'CHARGE' ? 'Нарахування' : 'Оплата'}
                          size="small"
                          color={entry.entryType === 'CHARGE' ? 'warning' : 'success'}
                        />
                        <Typography variant="body1" component="span">
                          ${entry.amount.toFixed(2)}
                        </Typography>
                        {entry.note && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {entry.note}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <span>{new Date(entry.entryDate).toLocaleDateString('uk-UA')}</span>
                        <span>Баланс: ${entry.runningBalance.toFixed(2)}</span>
                      </Box>
                    }
                  />
                );

                return (
                  <Paper key={entry.id} sx={{ mb: 1.5 }}>
                    <ListItem
                      disablePadding={entry.entryType === 'CHARGE' && !!entry.sourceActivityGroupId}
                      secondaryAction={
                        entry.entryType === 'PAYMENT' && (
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleDeletePayment(entry.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                      }
                    >
                      {entry.entryType === 'CHARGE' && entry.sourceActivityGroupId ? (
                        <ListItemButton
                          onClick={() => navigate(
                            `/companies/${companyId}/activities/group/${entry.sourceActivityGroupId}/edit`,
                            { state: { returnPath: `${window.location.pathname}${window.location.search}` } }
                          )}
                        >
                          {itemText}
                        </ListItemButton>
                      ) : itemText}
                    </ListItem>
                  </Paper>
                );
              })}
            </List>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                page={page + 1}
                count={totalPages}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}

      <RecordPaymentDialog
        open={dialogOpen}
        contactId={contactIdNum}
        direction={direction}
        onClose={() => setDialogOpen(false)}
        onSubmit={recordPayment}
      />
    </Box>
  );
}
